// =============================================================================
// TimeZoneSelect - Selettore di fuso orario con ricerca
// =============================================================================
// Un <select> nativo con ~400 voci è inusabile: qui si digita per filtrare
// (città, regione o offset) e si sceglie con mouse o tastiera.
//
// Ogni riga mostra tre informazioni, in ordine di utilità per chi sceglie:
//   • l'offset (GMT+02:00) — la conferma immediata di aver preso quello giusto
//   • il nome del fuso     — l'identificativo IANA, reso leggibile
//   • l'ora corrente lì    — il modo più veloce per riconoscere il proprio fuso
//                            senza sapere come si chiama
//
// Le voci sono ordinate per offset, così i fusi vicini stanno vicini. Di
// conseguenza righe adiacenti mostrano spesso lo stesso orario: è corretto,
// sono zone che condividono l'offset (tutta l'Europa continentale segna la
// stessa ora).
// =============================================================================

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  formatGmtOffset,
  timeStringInTz,
  timeZoneLabel,
  timeZoneOffsetMinutes,
  timeZoneSearchAliases,
} from '../../lib/tenantTime';

interface TimeZoneSelectProps {
  /** Identificativi IANA selezionabili. */
  timeZones: string[];
  value: string;
  onChange: (timeZone: string) => void;
  placeholder?: string;
  noMatchLabel?: string;
  disabled?: boolean;
  className?: string;
}

interface TimeZoneOption {
  id: string;
  label: string;
  offsetLabel: string;
  offsetMinutes: number;
  localTime: string;
  /** Testo normalizzato su cui gira la ricerca. */
  haystack: string;
}

export function TimeZoneSelect({
  timeZones,
  value,
  onChange,
  placeholder,
  noMatchLabel = '—',
  disabled = false,
  className,
}: TimeZoneSelectProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Offset e ora corrente si calcolano una volta sola per lista: sono ~400
  // conversioni, inutile rifarle a ogni battuta di ricerca.
  const options = useMemo<TimeZoneOption[]>(() => {
    const now = new Date();
    return timeZones
      .map((tz) => {
        const label = timeZoneLabel(tz);
        const offsetLabel = formatGmtOffset(tz, now);
        return {
          id: tz,
          label,
          offsetLabel,
          offsetMinutes: timeZoneOffsetMinutes(tz, now),
          localTime: timeStringInTz(now, tz),
          haystack: `${label} ${timeZoneSearchAliases(tz)} ${offsetLabel}`.toLowerCase(),
        };
      })
      .sort(
        (a, b) => a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label),
      );
  }, [timeZones]);

  const selected = useMemo(
    () => options.find((o) => o.id === value),
    [options, value],
  );

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Ogni termine deve comparire: "roma 02" o "gmt+02 europe" funzionano
    const terms = q.split(/\s+/);
    return options.filter((o) => terms.every((t) => o.haystack.includes(t)));
  }, [options, query]);

  // Riporta l'evidenziazione sulla voce selezionata a ogni apertura, così
  // aprendo il menu si parte da dove si è.
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.id === value);
    setHighlight(idx >= 0 ? idx : 0);
  }, [open, value, filtered]);

  // Tiene la voce evidenziata dentro la porzione visibile durante la
  // navigazione da tastiera.
  useEffect(() => {
    if (!open || !listRef.current) return;
    // Si cerca per data-attribute invece di indicizzare i figli: regge anche se
    // in futuro la lista contenesse righe non selezionabili.
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-option-index="${highlight}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  // Chiusura al click fuori
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const selectOption = (option: TimeZoneOption) => {
    onChange(option.id);
    close();
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => {
        if (filtered.length === 0) return 0;
        return (h + delta + filtered.length) % filtered.length;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[highlight]) selectOption(filtered[highlight]);
      else setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          disabled={disabled}
          // Chiuso mostra la selezione corrente; aperto diventa il campo di ricerca
          value={open ? query : selected ? `${selected.offsetLabel} · ${selected.label}` : ''}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full input pl-10 pr-9 py-2"
        />
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </div>

      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">{noMatchLabel}</li>
          ) : (
            filtered.map((option, idx) => (
              <li
                key={option.id}
                role="option"
                data-option-index={idx}
                aria-selected={option.id === value}
                onMouseEnter={() => setHighlight(idx)}
                // mousedown invece di click: previene il blur dell'input prima
                // che la selezione venga registrata
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectOption(option);
                }}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 text-sm cursor-pointer',
                  idx === highlight ? 'bg-emerald-50' : 'hover:bg-gray-50',
                )}
              >
                <span
                  className={cn(
                    'flex-shrink-0 w-[5.25rem] text-center rounded px-1.5 py-0.5',
                    'text-[11px] font-medium tabular-nums',
                    idx === highlight
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-gray-100 text-gray-600',
                  )}
                >
                  {option.offsetLabel}
                </span>
                <span className="flex-1 min-w-0 truncate text-gray-900">{option.label}</span>
                <span className="flex-shrink-0 text-xs text-gray-500 tabular-nums">
                  {option.localTime}
                </span>
                {option.id === value && (
                  <Check className="w-4 h-4 flex-shrink-0 text-emerald-600" />
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
